// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 26-versioning-update-strategy.md#trial-and-entitlement-copy
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { SectionCard } from '@/components/greenlight/SectionCard';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  FLEET_PAYMENT_METHOD_REASONS,
  type BillingSettings as BillingSettingsResponse,
  type FleetBillingSummary,
} from '@greenlight/shared';

import { SettingsSectionHeading } from './SettingsSectionHeading';

type Account = NonNullable<FleetBillingSummary['account']>;
type Variant = React.ComponentProps<typeof StatusBadge>['variant'];

export const BILLING_KEY = ['settings', 'billing'] as const;

/** A dated term is a calendar day in UTC; localizing it moves the date for viewers west of UTC. */
function formatDay(value: string): string {
  return new Date(value.length === 10 ? `${value}T00:00:00Z` : value).toLocaleDateString(
    undefined,
    {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    },
  );
}

const MARKETPLACES: Record<
  Extract<BillingSettingsResponse, { status: 'marketplace' }>['cloud'],
  { listing: string; vendor: string; console: string }
> = {
  azure: { listing: 'Azure Marketplace', vendor: 'Microsoft', console: 'the Azure portal' },
  aws: { listing: 'AWS Marketplace', vendor: 'AWS', console: 'the AWS Billing console' },
};

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const quantity = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

const PLURAL_UNITS: Record<string, string> = {
  app: 'apps',
  hour: 'hours',
  deployment: 'deployments',
  user: 'users',
};

function measured(amount: number, unit: string): string {
  return `${quantity.format(amount)} ${amount === 1 ? unit : (PLURAL_UNITS[unit] ?? unit)}`;
}

function Row({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1 px-5 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="min-w-0 text-sm font-medium text-foreground sm:text-right">{children}</span>
    </div>
  );
}

function entitlementOf(summary: FleetBillingSummary): {
  variant: Variant;
  label: string;
  detail: string | null;
} {
  const { state, reason, effective_until: until } = summary.entitlement;
  const trial = reason === 'trial_active' || reason === 'trial_ending';
  const card = (FLEET_PAYMENT_METHOD_REASONS as readonly string[]).includes(reason);
  if (card) {
    if (state === 'lapsed') {
      return {
        variant: 'failed',
        label: 'Payment method needed',
        detail: 'New building is paused until a payment method is saved',
      };
    }
    return {
      variant: state === 'grace' ? 'degraded' : 'pending',
      label: 'Payment method needed',
      detail: until && `Add one by ${formatDay(until)} to keep building`,
    };
  }
  if (state === 'entitled') {
    if (trial)
      return {
        variant: 'active',
        label: 'Free trial',
        detail: until && `Through ${formatDay(until)}`,
      };
    return { variant: 'active', label: 'Licensed', detail: until && `Through ${formatDay(until)}` };
  }
  if (state === 'grace') {
    return {
      variant: 'degraded',
      label: trial ? 'Trial ending' : 'Ending soon',
      detail: until && `New building pauses after ${formatDay(until)}`,
    };
  }
  if (state === 'lapsed') {
    return { variant: 'failed', label: 'Lapsed', detail: 'New building is paused' };
  }
  return { variant: 'inactive', label: 'Not confirmed', detail: null };
}

const STANDING: Record<Account['standing']['standing'], { variant: Variant; label: string }> = {
  current: { variant: 'active', label: 'Current' },
  grace: { variant: 'degraded', label: 'Payment past due' },
  lapsed: { variant: 'failed', label: 'Lapsed' },
  unknown: { variant: 'inactive', label: 'Not confirmed' },
};

const COLLECTION: Record<
  NonNullable<Account['last_settlement']>['collection_state'],
  { variant: Variant; label: string }
> = {
  priced: { variant: 'pending', label: 'Being prepared' },
  collection_pending: { variant: 'pending', label: 'Being collected' },
  collected: { variant: 'active', label: 'Invoiced' },
  collection_failed: { variant: 'failed', label: 'Payment failed' },
  manual: { variant: 'inactive', label: 'Invoiced by Shift' },
};

function amountOf(settlement: NonNullable<Account['last_settlement']>): string {
  return settlement.denomination === 'monetary'
    ? usd.format(settlement.net_minor / 100)
    : `${quantity.format(settlement.net_minor)} credits`;
}

function AccountRows({ account }: { account: Account }): React.JSX.Element {
  // `unknown` also covers a stale or conflicting payment signal; only the
  // absence of any signal means nothing has been paid yet.
  // A provider that hosts payment is the only source of signals, so "no signal"
  // means unpaid only there; a manual account never has one.
  const standing =
    account.standing.standing === 'unknown' &&
    account.standing.reason === 'no_signal' &&
    account.capability.hosted_management
      ? { variant: 'inactive' as const, label: 'No payment yet' }
      : STANDING[account.standing.standing];
  const last = account.last_settlement;
  return (
    <>
      <Row label="Account standing">
        <StatusBadge variant={standing.variant}>{standing.label}</StatusBadge>
      </Row>
      <Row label="Terms">
        {account.terms === null ? (
          <span className="font-normal text-muted-foreground">Not set yet</span>
        ) : (
          <span className="flex flex-col sm:items-end">
            <span>{account.terms.summary ?? account.terms.label}</span>
            <span className="text-xs font-normal text-muted-foreground">
              {account.terms.label} · version {account.terms.pricing_policy_version} · since{' '}
              {formatDay(account.terms.effective_from)}
            </span>
          </span>
        )}
      </Row>
      <Row label="Last bill">
        {last === null ? (
          <span className="font-normal text-muted-foreground">None yet</span>
        ) : (
          <span className="inline-flex flex-wrap items-center gap-2 sm:justify-end">
            <span>
              {amountOf(last)} for {formatDay(last.window.start)} –{' '}
              {formatDay(new Date(Date.parse(last.window.end) - 1).toISOString())}
            </span>
            <StatusBadge variant={COLLECTION[last.collection_state].variant}>
              {COLLECTION[last.collection_state].label}
            </StatusBadge>
          </span>
        )}
      </Row>
      {account.balance !== null && (
        <Row label="Balance">
          <span className="flex flex-col sm:items-end">
            <span>
              {quantity.format(account.balance.amount)} {account.balance.unit}
            </span>
            <span className="text-xs font-normal text-muted-foreground">
              As of {formatDay(account.balance.as_of)}
              {account.balance.expires_at !== null &&
                ` · expires ${formatDay(account.balance.expires_at)}`}
            </span>
          </span>
        </Row>
      )}
    </>
  );
}

function UsageRows({ usage }: { usage: FleetBillingSummary['usage'] }): React.JSX.Element {
  if (usage.length === 0) {
    return (
      <p className="px-5 py-4 text-sm text-muted-foreground">Nothing measured yet this month.</p>
    );
  }
  return (
    <div className="divide-y divide-border">
      {usage.map((meter) => (
        <Row key={meter.meter_key} label={meter.label}>
          <span className="flex flex-col sm:items-end">
            <span>{measured(meter.quantity, meter.unit)}</span>
            <span className="text-xs font-normal text-muted-foreground">
              {formatDay(meter.window.start)} to {formatDay(meter.window.end)}
              {meter.complete ? '' : ' · so far'}
            </span>
          </span>
        </Row>
      ))}
    </div>
  );
}

function PaymentAction({
  account,
  navigate,
}: {
  account: Account | null;
  navigate: (url: string) => void;
}): React.JSX.Element {
  const session = api.settings.requestBillingSession.useMutation();
  const [error, setError] = React.useState<string | null>(null);

  if (account === null) {
    return (
      <p className="text-sm text-muted-foreground">
        Shift hasn&rsquo;t set up a billing account for this install yet. Once it has, you can add a
        payment method here.
      </p>
    );
  }
  if (!account.capability.hosted_management) {
    return (
      <p className="text-sm text-muted-foreground">
        Payment for this install is arranged with Shift directly. Contact your Shift account team to
        change how you pay.
      </p>
    );
  }

  const open = async (): Promise<void> => {
    setError(null);
    try {
      const res = await session.mutateAsync({ body: {} });
      if (res.status !== 200) throw res;
      navigate(res.body.url);
    } catch (err) {
      setError(
        (err as { body?: { message?: string } })?.body?.message ??
          'Couldn’t open the payment page. Try again shortly.',
      );
    }
  };

  const label =
    account.payment_method_on_file === true ? 'Manage billing' : 'Set up payment method';
  return (
    <div className="space-y-3">
      <Button
        type="button"
        className="h-11 w-full sm:w-auto"
        onClick={() => void open()}
        disabled={session.isPending}
      >
        {session.isPending ? (
          <Loader2 className="size-4 animate-spin" aria-hidden />
        ) : (
          <ExternalLink className="size-4" aria-hidden />
        )}
        {label}
      </Button>
      <p className="text-xs text-muted-foreground">
        Opens a secure page hosted by Shift’s payment provider. Greenlight never sees card details.
      </p>
      {error !== null && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}

/**
 * Settings → Billing (docs/28 §/settings). Everything here is what Shift's fleet
 * monitor says about this install; the page computes no charge, standing, or
 * entitlement of its own, and never takes card details.
 */
export function BillingSettings({
  className,
  navigate = (url) => window.location.assign(url),
}: {
  className?: string;
  /** Where a hosted payment page opens; injectable for tests. */
  navigate?: (url: string) => void;
}): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const returned = searchParams.get('billing') === 'returned';
  const query = api.settings.getBilling.useQuery(BILLING_KEY, undefined, { queryKey: BILLING_KEY });
  const settings = query.data?.status === 200 ? query.data.body : null;

  let body: React.ReactNode;
  if (query.isLoading) {
    body = (
      <div className="space-y-2 px-5 py-4" aria-busy="true">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-4 w-56" />
      </div>
    );
  } else if (settings === null || settings.status === 'unavailable') {
    body = (
      <div className="px-5 py-4">
        <Alert variant="destructive">
          <AlertDescription>
            Couldn’t reach Shift for billing details. This doesn’t affect your apps. Retry in a
            moment.
          </AlertDescription>
        </Alert>
      </div>
    );
  } else if (settings.status === 'marketplace') {
    const marketplace = MARKETPLACES[settings.cloud];
    body = (
      <div className="space-y-2 px-5 py-4 text-sm">
        <p className="font-medium text-foreground">Billed through {marketplace.listing}</p>
        <p className="text-muted-foreground">
          You bought Greenlight through {marketplace.listing}, so {marketplace.vendor} bills you for
          it with your other cloud charges. Your subscription, invoices, and payment method are
          managed in {marketplace.console}, not here.
        </p>
      </div>
    );
  } else if (settings.status === 'not_enrolled') {
    body = (
      <p className="px-5 py-4 text-sm text-muted-foreground">
        This install hasn’t connected to Shift yet. Billing details appear once its first health
        report is accepted.
      </p>
    );
  } else {
    const summary = settings.summary;
    const entitlement = entitlementOf(summary);
    body = (
      <div className="divide-y divide-border">
        <Row label="License">
          <span className="inline-flex flex-wrap items-center gap-2 sm:justify-end">
            <StatusBadge variant={entitlement.variant}>{entitlement.label}</StatusBadge>
            {entitlement.detail !== null && (
              <span className="font-normal text-muted-foreground">{entitlement.detail}</span>
            )}
          </span>
        </Row>
        {summary.account === null ? (
          <Row label="Billing account">
            <span className="font-normal text-muted-foreground">Not set up yet</span>
          </Row>
        ) : (
          <AccountRows account={summary.account} />
        )}
      </div>
    );
  }

  const summary = settings?.status === 'available' ? settings.summary : null;

  return (
    <div className={cn('space-y-6', className)}>
      <SettingsSectionHeading title="Billing" pill="Org admin" />

      {returned && (
        <Alert>
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>
              Back from the payment page. A saved payment method can take a minute to show here
              while Shift confirms it.
            </span>
            <Button
              type="button"
              variant="outline"
              className="h-11"
              onClick={() => void query.refetch()}
            >
              <RefreshCw className="size-4" aria-hidden />
              Refresh
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <SectionCard headerBar title="Your plan">
        {body}
      </SectionCard>

      {summary !== null && (
        <SectionCard headerBar title="Measured usage">
          <UsageRows usage={summary.usage} />
        </SectionCard>
      )}

      {summary !== null && (
        <SectionCard headerBar title="Payment method">
          <div className="px-5 py-4">
            <PaymentAction account={summary.account} navigate={navigate} />
          </div>
        </SectionCard>
      )}

      {settings?.status === 'marketplace' ? (
        <p className="text-sm text-muted-foreground">
          If your subscription lapses, apps already deployed keep running and serving their users.
          Only new building pauses, after a warning.
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">
          Measured usage is what this install has reported, not an invoice. This install sends a
          health report to Shift Engineering on a regular schedule; that report is how Shift
          confirms it is licensed. If a payment lapses, apps already deployed keep running and
          serving their users. Only new building pauses, after a warning that names the date.
        </p>
      )}
    </div>
  );
}
